import { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import {
  createClinicalRoleApi,
  listClinicalRolesApi,
} from "../services/clinicalForms.services";
import {
  mergeBuiltinAndApiRoles,
  toClinicalRole,
  toClinicalRoles,
  toCreateRolePayload,
} from "../mappers/clinicalForms.mappers";
import { defaultRoleForms } from "../domain/clinicalForms";

const useClinicalRoles = () => {
  const [apiRoles, setApiRoles] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const roles = useMemo(
    () => mergeBuiltinAndApiRoles(apiRoles, overrides),
    [apiRoles, overrides]
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listClinicalRolesApi();
      const list = Array.isArray(data) ? data : data?.results || data?.data || [];
      const next = toClinicalRoles(list);
      setApiRoles(next);
      return next;
    } catch {
      message.error("Failed to load roles");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const create = useCallback(async (input) => {
    setCreating(true);
    try {
      const { data } = await createClinicalRoleApi(toCreateRolePayload(input));
      const created = toClinicalRole(data);
      const seeded = { ...created, forms: defaultRoleForms() };
      setApiRoles((prev) => [...prev, seeded]);
      message.success("Role created");
      return seeded;
    } catch (err) {
      message.error(err?.response?.data?.detail || "Failed to create role");
      throw err;
    } finally {
      setCreating(false);
    }
  }, []);

  const updateLocal = useCallback((next) => {
    if (String(next.id).startsWith("builtin_")) {
      setOverrides((prev) => ({ ...prev, [next.id]: next }));
      return;
    }

    setApiRoles((prev) =>
      prev.map((role) => (role.id === next.id ? { ...role, ...next } : role))
    );
    setOverrides((prev) => {
      if (!prev[next.id]) return prev;
      const nextOverrides = { ...prev };
      delete nextOverrides[next.id];
      return nextOverrides;
    });
  }, []);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { roles, loading, creating, refresh, create, updateLocal };
};

export default useClinicalRoles;
